import { makeElement, type DesignElement, type Design } from "./design";

export function starterDesign(id = "welcome"): Design {
  const elements: DesignElement[] = [];
  const add = (type: DesignElement["type"], v: Partial<DesignElement>) => {
    const e = makeElement(type, v);
    elements.push(e);
    return e.id;
  };
  const desktop = add("frame", {
    id: "desktop",
    name: "Orbit · Desktop",
    x: 0,
    y: 0,
    width: 880,
    height: 620,
    fill: "#f9f9f9",
    radius: 12,
  });
  const shape = (
    name: string,
    x: number,
    y: number,
    width: number,
    height: number,
    fill: string,
    extra: Partial<DesignElement> = {},
  ) =>
    add("rectangle", {
      name,
      parentId: desktop,
      x,
      y,
      width,
      height,
      fill,
      ...extra,
    });
  const label = (
    name: string,
    text: string,
    x: number,
    y: number,
    width: number,
    fontSize = 14,
    color = "#7b7b7b",
    weight = 400,
    extra: Partial<DesignElement> = {},
  ) =>
    add("text", {
      name,
      text,
      parentId: desktop,
      x,
      y,
      width,
      height: fontSize * 1.5,
      fontSize,
      color,
      fontWeight: weight,
      ...extra,
    });
  shape("Sidebar", 0, 0, 180, 620, "#ffffff");
  shape("Divider", 179, 0, 1, 620, "#e9e9e9");
  shape("Orbit mark", 23, 28, 24, 24, "#e3e3e3", { radius: 8 });
  label("Logo", "orbit", 58, 26, 100, 23, "#252525", 700);
  label("Workspace label", "WORKSPACE", 24, 88, 140, 10, "#7f7f7f", 600);
  shape("Active navigation", 12, 120, 155, 38, "#f0f0f0", { radius: 6 });
  label("Overview link", "◈   Overview", 25, 130, 140, 13, "#3e3e3e", 600);
  label("Projects link", "▦   Projects", 25, 177, 140, 13);
  label("Tasks link", "☑   My tasks", 25, 222, 140, 13);
  label("Notes link", "▤   Notes", 25, 267, 140, 13);
  shape("Upgrade card", 14, 454, 151, 111, "#f4f4f4", { radius: 10 });
  label(
    "Upgrade title",
    "A little more space.",
    26,
    469,
    128,
    12,
    "#303030",
    600,
  );
  label("Upgrade detail", "Make room for big ideas.", 26, 493, 132, 10);
  add("button", {
    name: "Upgrade button",
    parentId: desktop,
    x: 26,
    y: 519,
    width: 125,
    height: 30,
    text: "Explore Pro  ↗",
    fontSize: 11,
    fill: "#ffffff",
    color: "#383838",
    stroke: "#e5e5e5",
    radius: 5,
  });
  label("Account", "JD   Jamie Davis", 24, 588, 145, 12, "#454545", 500);
  label("Breadcrumb", "Workspace  /  Overview", 210, 29, 420, 12);
  shape("Header divider", 180, 67, 700, 1, "#e9e9e9");
  label(
    "Greeting",
    "A little focus. A lot of progress.",
    211,
    101,
    615,
    28,
    "#2e2e2e",
    600,
  );
  label(
    "Greeting description",
    "Everything you’re working on, in one good place.",
    212,
    147,
    595,
    13,
  );
  add("button", {
    id: "new-project-button",
    name: "New project",
    parentId: desktop,
    x: 712,
    y: 28,
    width: 139,
    height: 30,
    text: "+ New project",
    fontSize: 11,
    radius: 6,
  });
  [
    ["Active projects", "08", "2 started this week"],
    ["Tasks completed", "24", "8 more than last week"],
    ["Focus time", "32h", "Looking good this week"],
  ].forEach((a, i) => {
    const x = 212 + i * 216;
    shape(a[0] + " card", x, 199, 202, 127, i === 0 ? "#ececec" : "#ffffff", {
      radius: 10,
      stroke: i === 0 ? "#e2e2e2" : "#e7e7e7",
    });
    label(a[0] + " label", a[0], x + 17, 215, 174, 12, "#5e5e5e", 500);
    label(a[0] + " value", a[1], x + 17, 241, 170, 31, "#313131", 600);
    label(a[0] + " trend", a[2], x + 17, 291, 174, 10, "#6e6e6e");
  });
  label("Projects heading", "Your projects", 212, 365, 450, 18, "#2e2e2e", 600);
  label("View all", "View all  ↗", 769, 369, 84, 11, "#686868");
  [
    ["Website refresh", "Design", "12 / 18 tasks", "#e3e3e3", 0.68],
    ["Mobile experience", "Product", "8 / 24 tasks", "#c7c7c7", 0.34],
    ["Brand guidelines", "Branding", "16 / 20 tasks", "#cccccc", 0.8],
  ].forEach((row, i) => {
    const y = 405 + i * 61;
    shape("Project row " + i, 212, y, 634, 53, "#ffffff", {
      radius: 7,
      stroke: "#e9e9e9",
    });
    shape("Project icon " + i, 225, y + 12, 28, 28, String(row[3]), {
      radius: 7,
    });
    label(
      "Project title " + i,
      String(row[0]),
      266,
      y + 9,
      210,
      12,
      "#363636",
      600,
    );
    label("Project category " + i, String(row[1]), 266, y + 29, 200, 10);
    label("Task count " + i, String(row[2]), 538, y + 10, 108, 10);
    shape("Progress track " + i, 538, y + 32, 116, 4, "#efefef", { radius: 2 });
    shape("Progress " + i, 538, y + 32, 116 * Number(row[4]), 4, "#8e8e8e", {
      radius: 2,
    });
    label(
      "Project status " + i,
      i === 2 ? "In review" : "In progress",
      723,
      y + 19,
      95,
      10,
      "#6d6d6d",
    );
  });
  const mobile = add("frame", {
    id: "mobile",
    name: "Orbit · Mobile",
    x: 940,
    y: 0,
    width: 300,
    height: 620,
    fill: "#f9f9f9",
    radius: 24,
  });
  const ml = (
    name: string,
    text: string,
    x: number,
    y: number,
    width: number,
    fontSize = 14,
    extra: Partial<DesignElement> = {},
  ) =>
    add("text", {
      parentId: mobile,
      name,
      text,
      x,
      y,
      width,
      height: fontSize * 1.5,
      fontSize,
      ...extra,
    });
  ml("Mobile time", "9:41", 24, 17, 100, 11, { fontWeight: 600 });
  ml("Mobile status", "● ▰", 245, 17, 40, 11);
  ml("Mobile logo", "orbit", 24, 65, 150, 27, { fontWeight: 700 });
  ml("Mobile menu", "☰", 255, 68, 25, 20);
  ml("Mobile greeting", "Make space\nfor good work.", 24, 127, 255, 32, {
    fontWeight: 600,
    height: 89,
  });
  ml("Mobile subtitle", "Here’s where things stand today.", 24, 229, 258, 12, {
    color: "#797979",
  });
  add("rectangle", {
    parentId: mobile,
    name: "Mobile summary",
    x: 24,
    y: 276,
    width: 252,
    height: 131,
    fill: "#ececec",
    radius: 14,
  });
  ml("Mobile active label", "Active projects", 42, 294, 200, 12, {
    color: "#5e5e5e",
  });
  ml("Mobile active count", "08", 42, 319, 220, 40, { fontWeight: 600 });
  ml("Mobile active trend", "↗  2 started this week", 42, 378, 200, 11, {
    color: "#5e5e5e",
  });
  ml("Mobile projects heading", "Your projects", 24, 443, 250, 18, {
    fontWeight: 600,
  });
  add("rectangle", {
    parentId: mobile,
    name: "Mobile project card",
    x: 24,
    y: 482,
    width: 252,
    height: 70,
    fill: "#ffffff",
    stroke: "#e7e7e7",
    radius: 10,
  });
  ml("Mobile project title", "Website refresh", 40, 496, 230, 13, {
    fontWeight: 600,
  });
  ml("Mobile project detail", "Design  ·  12 / 18 tasks", 40, 522, 230, 11, {
    color: "#797979",
  });
  add("rectangle", {
    parentId: mobile,
    name: "Home indicator",
    x: 110,
    y: 600,
    width: 80,
    height: 4,
    fill: "#2e2e2e",
    radius: 2,
  });
  const now = new Date().toISOString();
  return {
    id,
    name: "Orbit workspace",
    revision: 0,
    elements,
    activity: [],
    createdAt: now,
    updatedAt: now,
  };
}
